"use client";
import BlogAreaCard from "@/components/common/blog-area-card";
import SearchBar from "@/components/blog/SearchBar";
import LoadMore from "@/components/blog/LoadMore";
import { getAllBlogsClient, getAllTagsClient } from "@/lib/blog-data";
import { useMemo, useState, useCallback } from "react";

const DynamicBlogArea = () => {
    const [activeTag, setActiveTag] = useState<string>("All");
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [displayCount, setDisplayCount] = useState<number>(9);
    const [loading, setLoading] = useState<boolean>(false);
    const itemsPerLoad = 9;
	// const MAX_TAGS = 7; // Show only first 7 most frequent tags
    
    const blogs = useMemo(() => getAllBlogsClient(), []);

	const tags = useMemo(() => {
		const allTags = getAllTagsClient();
		
		// Define the desired tag order (excluding "All" which will be added first)
		const tagOrder = [
			"strategy-ideation",
			"digital-product-experiences",
			"technology-transformation",
			"go-to-market-growth-systems",
			"news-insights"
		];
		
		// Sort tags according to the defined order, then add any remaining tags
		const sortedTags: string[] = [];
		const remainingTags = new Set(allTags);
		
		// Add tags in the specified order
		tagOrder.forEach(tag => {
			if (remainingTags.has(tag)) {
				sortedTags.push(tag);
				remainingTags.delete(tag);
			}
		});
		
		// Add any remaining tags that weren't in the order list (alphabetically)
		const remaining = Array.from(remainingTags).sort();
		sortedTags.push(...remaining);
		
		return ["All", ...sortedTags];
	}, []);

    const filtered = useMemo(() => {
        let filteredBlogs = blogs;
        
        // Filter by tag (exact match)
        if (activeTag !== "All") {
            filteredBlogs = filteredBlogs.filter(blog => 
                blog.tags.some(tag => tag.toLowerCase() === activeTag.toLowerCase())
            );
        }
        
        // Filter by search query
        if (searchQuery) {
            filteredBlogs = filteredBlogs.filter(blog => 
                blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                blog.short_description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                blog.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())) ||
                blog.author.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }
        
        return filteredBlogs;
    }, [activeTag, searchQuery, blogs]);

    const displayedBlogs = useMemo(() => {
        return filtered.slice(0, displayCount);
    }, [filtered, displayCount]);

    const hasMore = displayCount < filtered.length;


    const handleSearch = useCallback((query: string) => {
        // Only reset display count if the search query actually changed
        if (query !== searchQuery) {
            setDisplayCount(9); // Reset to initial count when searching
        }
        setSearchQuery(query);
    }, [searchQuery]);

    const handleLoadMore = () => {
        if (loading) return; // Prevent multiple clicks
        
        setLoading(true);
        
        // Simulate loading delay
        setTimeout(() => {
            setDisplayCount(prev => {
                const newCount = Math.min(prev + itemsPerLoad, filtered.length);
                return newCount;
            });
            setLoading(false);
        }, 300); // Reduced delay for smoother experience
    };

    // Convert blog data to the format expected by BlogAreaCard
    const blogCards = useMemo(() => {
        return displayedBlogs.map(blog => ({
            title: blog.title,
            date: blog.date || new Date().toISOString().split('T')[0],
            imageUrl: "/images/blog/blog-thumb-1.png", // Default image
            url: `/blog/${blog.slug}`,
            tag: blog.tags[0] || "General",
            description: blog.short_description,
            author: blog.author
        }));
    }, [displayedBlogs]);

    return (
        <section className={`desilo-blog-section section-padding-top overflow-hidden`}>
            <div className="container custom-container">
                {/* Search Bar */}
                <div className="row mb-4 align-items-center">
                    <div className="col-lg-8 d-lg-block d-none ">
                            <div className="d-flex gap-2 flex-wrap">
                                {tags.map((t) => (
                                    <span
                                        key={t}
                                        className={`${activeTag === t ? "bg-color-2" : ""} service-tags cursor-pointer`}
                                        onClick={() => {
                                            setActiveTag(t);
                                            setDisplayCount(9); // Reset to initial count when filtering
                                        }}
                                    >
                                        {t}
                                    </span>
                                ))}
                            </div>
                    </div>
                    <div className="col-lg-4 col-12">
                        <SearchBar onSearch={handleSearch} placeholder="Search" />
                    </div>
                </div>
                
                    {/* Results Info */}
                    <div className="col-12 mb-3 ">
                        <div className="">
                            {searchQuery && (
                                <h3 className="">
                                    Search Results Found For: &quot;{searchQuery}&quot;
                                </h3>
                            )}
                        </div>
                    </div>
                {/* Tag Filters */}
                <div className="row gx-4 gy-5" style={{ minHeight: '200px' }}>
                    {/* blog card start */}
                    {blogCards.map((item, i) => (
                        <div
                            className="col-md-6 col-lg-4 blog-card-item"
                            key={`${item.url}-${i}`}
                            style={{
                                animation: 'fadeInUp 0.5s ease-out',
                                animationDelay: `${i * 0.1}s`,
                                animationFillMode: 'both'
                            }}
                        >
                            <BlogAreaCard data={item} />
                        </div>
                    ))}
                    {/* blog card end */}
                </div>
 
                {/* Load More Button */}
                <LoadMore 
                    onLoadMore={handleLoadMore}
                    hasMore={hasMore}
                    loading={loading}
                />
            </div>
        </section>
    );
};

export default DynamicBlogArea;
